import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Finding, PaymentSync } from '../../api/health';
import { formatAge } from '../../api/queueMonitor';
import { TRIGGER_ICON, TRIGGER_LABEL, toneOf, worstOf } from './AppFrame';
import { BoardFunction } from './boardModel';

export interface IFunctionChipProps {
    fn: BoardFunction;
    /** The findings about the queue this function reads; it is the one that has to act. */
    findings: Finding[];
    /** How far the payment sync has got, for the timer that does it. */
    paymentSync?: PaymentSync | null;
    onOpen: (appKey: string) => void;
}

/** A moment as day, month and time, with the year only when it is not this one. */
const formatMoment = (iso?: string | null): string | null => {
    if (!iso) {
        return null;
    }

    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toLocaleString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year:
            date.getFullYear() === new Date().getFullYear()
                ? undefined
                : 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * The line under the payment timer: is the route on, and how far has it got.
 *
 * This is the one signal on the board that does not depend on the runtime's bookkeeping. The
 * timer keeps its progress in the connector's own reference table, so even where the apps
 * themselves are out of sight this says whether payments are being looked for at all.
 */
const describePaymentSync = (
    sync: PaymentSync | null | undefined
): { text: string; hint: string; tone: 'neutral' | 'warn' } | null => {
    if (!sync) {
        return null;
    }

    if (sync.error) {
        return {
            text: 'voortgang onleesbaar',
            hint: sync.error,
            tone: 'warn',
        };
    }

    if (sync.enabled !== true) {
        return {
            text: 'staat uit',
            hint: `De feature SyncOfflinePaymentsUsingReceivables ${
                sync.enabled === false ? 'staat uit' : 'bestaat niet'
            } in deze omgeving. Deze timer stopt dan meteen, dus betalingen uit Exact lopen niet via deze route.`,
            tone: 'warn',
        };
    }

    const upTo = formatMoment(sync.processedUpTo);

    if (!upTo) {
        return {
            text: 'nog niets verwerkt',
            hint: 'De timer heeft nog geen voortgang weggeschreven. Hij begint dan bij receivables die na 30-09-2024 zijn gewijzigd.',
            tone: 'neutral',
        };
    }

    const advanced = formatAge(sync.lastAdvancedAt);

    return {
        text: `t/m ${upTo}`,
        hint: `Receivables die in Exact tot en met ${upTo} zijn gewijzigd, zijn verwerkt${
            advanced ? ` (bijgewerkt ${advanced} geleden)` : ''
        }. De volgende run kijkt alleen naar wat daarna is gewijzigd: een betaling van eerder die niet in Chargebee is aangekomen, haalt deze timer dus niet opnieuw op.`,
        tone: 'neutral',
    };
};

/**
 * One function, as a small pill: what sets it off, and its name as Application Insights
 * knows it.
 *
 * Deliberately light. The app around it carries the state and the links; the chip only turns
 * red or amber when something is wrong with the queue it reads, because that is the function
 * somebody has to look at.
 */
export const FunctionChip = ({
    fn,
    findings,
    paymentSync,
    onOpen,
}: IFunctionChipProps) => {
    const tone = toneOf(worstOf(findings));
    const sync =
        fn.role === 'payments' ? describePaymentSync(paymentSync) : null;

    const title = [
        `${fn.name} (${TRIGGER_LABEL[fn.trigger]})`,
        fn.reads ? `Leest ${fn.reads}.` : null,
        sync?.hint ?? null,
        findings.length > 0
            ? findings.map((finding) => `• ${finding.title}`).join('\n')
            : null,
        'Klik om te zien wat deze app heeft gedaan.',
    ]
        .filter(Boolean)
        .join('\n\n');

    return (
        <button
            type="button"
            className={`function-chip function-chip--${tone}`}
            onClick={() => onOpen(fn.appKey)}
            title={title}
        >
            <span className="function-chip__head">
                <FontAwesomeIcon
                    icon={
                        tone === 'bad'
                            ? 'circle-xmark'
                            : tone === 'warn'
                            ? 'triangle-exclamation'
                            : TRIGGER_ICON[fn.trigger]
                    }
                    className="function-chip__icon"
                />
                <code className="function-chip__name">{fn.name}</code>
            </span>

            {sync && (
                <span
                    className={`function-chip__note function-chip__note--${sync.tone}`}
                >
                    {sync.text}
                </span>
            )}
        </button>
    );
};
